// ═══════════════════════════════════════════════════════════════
// FLOW CHECK PANEL v1.0
// Self-contained component for steady-state flow verification
// ═══════════════════════════════════════════════════════════════
import { useState } from "react";
import { C } from "../../../constants";
import { runFlowCheck, runOptimizer } from "../calc/flowEstimationEngine";

export default function FlowCheckPanel({ encResult, pipeNetwork, formData, isMetric, onBomUpdate }) {
  const [status, setStatus] = useState("idle"); // idle | running | passed | failed | optimizing
  const [flowResult, setFlowResult] = useState(null);
  const [optimizeResult, setOptimizeResult] = useState(null);

  if (!pipeNetwork || !pipeNetwork.network || pipeNetwork.network.nodes.length <= 1) return null;

  const handleRunCheck = () => {
    setStatus("running");
    setOptimizeResult(null);
    setTimeout(() => {
      const result = runFlowCheck(pipeNetwork, encResult, formData);
      setFlowResult(result);
      if (result.error) { setStatus("failed"); return; }
      if (result.passed) {
        setStatus("passed");
        // Update BOM with verified nozzle data
        if (onBomUpdate && result.nozzleResults) {
          onBomUpdate(result);
        }
      } else {
        setStatus("failed");
      }
    }, 300);
  };

  const handleOptimize = () => {
    setStatus("optimizing");
    setTimeout(() => {
      const optResult = runOptimizer(pipeNetwork, encResult, formData);
      setOptimizeResult(optResult);
      if (optResult.success) {
        setFlowResult(optResult.result ? { ...optResult.result, passed: true, issues: [], warnings: optResult.check ? optResult.check.warnings : [] } : flowResult);
        setStatus("passed");
        if (onBomUpdate && optResult.result) {
          onBomUpdate(optResult.result);
        }
      } else {
        setStatus("failed");
      }
    }, 500);
  };

  const uP = "bar";

  return (
    <div style={{ marginTop: 8 }}>
      {/* Run Flow Check Button */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button onClick={handleRunCheck} disabled={status === "running" || status === "optimizing"}
          style={{
            padding: "9px 20px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 700, cursor: status === "running" ? "not-allowed" : "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", gap: 6,
            background: status === "passed" ? "#D1FAE5" : "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)",
            color: status === "passed" ? "#065F46" : C.white,
            boxShadow: status === "passed" ? "none" : "0 3px 12px rgba(30,64,175,0.25)",
            opacity: (status === "running" || status === "optimizing") ? 0.7 : 1,
          }}>
          {status === "running" ? "⏳ Checking..." : status === "passed" ? "✓ System Verified — Re-run" : status === "optimizing" ? "⏳ Optimizing..." : "🔬 Run Flow Check"}
        </button>
        {status === "idle" && (
          <span style={{ fontSize: 11, color: C.t3 }}>Verify system performance based on your pipe network design</span>
        )}
        {status === "passed" && flowResult && (
          <span style={{ fontSize: 11, color: "#065F46", fontWeight: 600 }}>
            ✓ Discharge: {flowResult.dischargeTime.toFixed(1)}s | Min nozzle pressure: {Math.min(...flowResult.nozzleResults.map(n => n.pressure)).toFixed(2)} {uP}
          </span>
        )}
      </div>

      {/* PASSED — System Performance Summary */}
      {status === "passed" && flowResult && (
        <div style={{ marginTop: 12, padding: 16, borderRadius: 10, background: "#ECFDF5", border: "1px solid #A7F3D0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#059669", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 14, fontWeight: 700 }}>✓</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#065F46" }}>System Verified — Meets NFPA 2001 Requirements</div>
              <div style={{ fontSize: 11, color: "#047857" }}>Design performs within acceptable parameters</div>
            </div>
          </div>

          {/* Key Metrics */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            {[
              ["Discharge Time", flowResult.dischargeTime.toFixed(1) + "s", "≤ 10s"],
              ["Agent in Pipe", flowResult.pctAgentInPipe.toFixed(1) + "%", "≤ 82.1%"],
              ["Start Pressure", flowResult.startPressure.toFixed(1) + " bar", ""],
              ["Before 1st Tee", flowResult.pctAgentBeforeFirstTee === "N/A" ? "N/A" : flowResult.pctAgentBeforeFirstTee.toFixed(1) + "%", flowResult.pctAgentBeforeFirstTee === "N/A" ? "" : ">= 9.3%"],
            ].map(([label, val, limit], i) => (
              <div key={i} style={{ background: "white", borderRadius: 8, padding: "8px 10px", border: "1px solid #D1FAE5" }}>
                <div style={{ fontSize: 10, color: "#047857", textTransform: "uppercase" }}>{label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#065F46" }}>{val}</div>
                {limit && <div style={{ fontSize: 9, color: "#6EE7B7" }}>{limit}</div>}
              </div>
            ))}
          </div>

          {/* Nozzle Results Table */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "#065F46", marginBottom: 6 }}>Nozzle Performance</div>
          <div style={{ background: "white", borderRadius: 8, border: "1px solid #D1FAE5", overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 90px 90px 90px", padding: "6px 10px", background: "#059669", color: "white", fontSize: 10, fontWeight: 600, textTransform: "uppercase" }}>
              <span>Nozzle</span><span>Part Number</span><span style={{ textAlign: "right" }}>Pressure</span><span style={{ textAlign: "right" }}>Flow Rate</span><span style={{ textAlign: "right" }}>Orifice Area</span>
            </div>
            {flowResult.nozzleResults.map((nr, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "60px 1fr 90px 90px 90px", padding: "6px 10px", borderBottom: i < flowResult.nozzleResults.length - 1 ? "1px solid #D1FAE5" : "none", fontSize: 11 }}>
                <span style={{ fontWeight: 600, color: "#065F46" }}>E{(i+1)}-N1</span>
                <span style={{ fontFamily: "monospace", fontSize: 10, color: "#047857" }}>{nr.selectedNozzle ? nr.selectedNozzle.partNumber : "—"}</span>
                <span style={{ textAlign: "right", color: nr.pressure >= 4.69 ? "#065F46" : "#DC2626" }}>{nr.pressure.toFixed(3)} {uP}</span>
                <span style={{ textAlign: "right", color: "#065F46" }}>{nr.flowRateLbs.toFixed(2)} lb/s</span>
                <span style={{ textAlign: "right", color: "#065F46" }}>{nr.selectedNozzle ? nr.selectedNozzle.orificeAreaMM2.toFixed(1) + " mm²" : "—"}</span>
              </div>
            ))}
          </div>

          {/* Optimizer fixes applied */}
          {optimizeResult && optimizeResult.fixes && optimizeResult.fixes.length > 0 && (
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "#FEF3C7", border: "1px solid #FDE68A" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#92400E", marginBottom: 4 }}>System optimized — changes applied:</div>
              {optimizeResult.fixes.map((fix, i) => (
                <div key={i} style={{ fontSize: 11, color: "#78350F", padding: "2px 0" }}>
                  • {fix.type === "pipe_diameter" ? `Pipe ${fix.from}→${fix.to}: ${fix.oldValue} → ${fix.newValue}` : fix.type === "cylinder_upsize" ? `Cylinder: ${fix.oldValue} → ${fix.newValue}` : JSON.stringify(fix)}
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {flowResult.warnings && flowResult.warnings.length > 0 && (
            <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "#FFFBEB", border: "1px solid #FDE68A" }}>
              {flowResult.warnings.map((w, i) => (
                <div key={i} style={{ fontSize: 10, color: "#92400E", padding: "1px 0" }}>⚠ {w.message}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAILED — Diagnostic Panel */}
      {status === "failed" && flowResult && (
        <div style={{ marginTop: 12, padding: 16, borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 14, fontWeight: 700 }}>✕</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#991B1B" }}>System Does Not Meet Requirements</div>
              <div style={{ fontSize: 11, color: "#B91C1C" }}>{flowResult.error || (flowResult.issues ? flowResult.issues.length + " issue(s) found" : "Unknown error")}</div>
            </div>
          </div>

          {/* Issues list */}
          {flowResult.issues && flowResult.issues.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {flowResult.issues.map((issue, i) => (
                <div key={i} style={{ display: "flex", gap: 8, padding: "8px 12px", borderRadius: 6, background: "white", border: "1px solid #FECACA", marginBottom: 4, fontSize: 12 }}>
                  <span style={{ color: "#DC2626", fontWeight: 700, flexShrink: 0 }}>✕</span>
                  <div>
                    <span style={{ color: "#991B1B" }}>{issue.message}</span>
                    {issue.value !== undefined && issue.limit !== undefined && (
                      <span style={{ display: "block", fontSize: 10, color: "#B91C1C", marginTop: 2 }}>
                        Value: {typeof issue.value === "number" ? issue.value.toFixed(2) : issue.value} | Limit: {typeof issue.limit === "number" ? issue.limit.toFixed(2) : issue.limit}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={handleOptimize} disabled={status === "optimizing"}
              style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #D97706 0%, #F59E0B 100%)", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 3px 12px rgba(217,119,6,0.25)" }}>
              {status === "optimizing" ? "⏳ Optimizing..." : "⚡ Optimize System"}
            </button>
            <span style={{ fontSize: 11, color: C.t3, alignSelf: "center" }}>
              Auto-adjust pipe diameters and cylinder size to meet requirements
            </span>
          </div>

          {/* Optimizer failed message */}
          {optimizeResult && !optimizeResult.success && (
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "#FEF3C7", border: "1px solid #FDE68A" }}>
              <p style={{ fontSize: 12, color: "#92400E", margin: 0 }}>
                <strong>Auto-optimization could not fully resolve all issues.</strong> Try manually adjusting pipe diameters in the pipe network drawing (use larger diameters for longer runs) or consider splitting the system across multiple cylinders.
              </p>
              {optimizeResult.fixes && optimizeResult.fixes.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: "#78350F" }}>Partial fixes attempted:</div>
                  {optimizeResult.fixes.map((fix, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#78350F", padding: "1px 0" }}>• {fix.type}: {fix.oldValue} → {fix.newValue}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Warnings */}
          {flowResult.warnings && flowResult.warnings.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {flowResult.warnings.map((w, i) => (
                <div key={i} style={{ fontSize: 10, color: "#92400E", padding: "1px 0" }}>⚠ {w.message}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
